import { useEffect, useState, type FormEvent } from "react";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { ApiKey, SettingsData, WebhookEndpoint, WebhookEvent } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { IconBook, IconExternal, IconPlus, IconRefresh } from "../components/Icons.tsx";
import { OnceWarning, SecretField } from "../components/SecretReveal.tsx";
import { StepUpDialog } from "../components/StepUp.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, CopyButton, Details, EmptyState, Time, Hash, PageHeader, Spinner, StatusPill } from "../components/ui.tsx";
import { formatTime, formatTimeFull, timeAgo } from "../lib/format.ts";
import { useAsync } from "../lib/hooks.ts";
import { useClientPages, usePagedList } from "../lib/paging.ts";
import { Pager } from "../components/Pager.tsx";
import { useSession } from "../lib/session.tsx";
import { fmt, plural, rich, useT } from "../i18n/index.tsx";

export function Developers() {
  const { can } = useSession();
  const t = useT();
  const keysView = can("api_keys:view");
  const hookView = can("webhook:view");
  const readOnly = (keysView && !can("api_keys:create") && !can("api_keys:revoke")) || (hookView && !can("webhook:edit"));
  return (
    <div className="page">
      <PageHeader
        title={t.developers.title}
        actions={
          <a className="btn btn-ghost" href="https://icryptonow.com/docs/" target="_blank" rel="noreferrer noopener">
            <IconBook size={18} /> {t.common.apiDocs} <IconExternal size={14} />
          </a>
        }
      >
        {t.developers.intro}
        {readOnly && t.developers.introReadOnly}
      </PageHeader>
      {hookView && <Integration write={can("webhook:edit")} />}
      {keysView && <ApiKeys />}
      {hookView && <Webhook write={can("webhook:edit")} />}
      {hookView && <Deliveries write={can("webhook:edit")} />}
    </div>
  );
}

// ---------------- Integration (API or hosted payment page) ----------------

type IntegrationKind = "api" | "hosted";

function Integration({ write }: { write: boolean }) {
  const t = useT();
  const toast = useToast();
  const q = useAsync(() => api.get<{ integration: IntegrationKind }>("/integration"), []);
  const [switchTo, setSwitchTo] = useState<IntegrationKind | null>(null);
  const current = q.data?.integration;
  const name = (k: IntegrationKind) => (k === "api" ? t.developers.integrationApi : t.developers.integrationHosted);
  const options: { kind: IntegrationKind; body: string }[] = [
    { kind: "api", body: t.developers.integrationApiBody },
    { kind: "hosted", body: t.developers.integrationHostedBody },
  ];
  return (
    <section className="card section-block" aria-labelledby="int-h">
      <div className="card-head">
        <div className="card-head-title">
          <div>
            <h2 id="int-h" className="card-title">{t.developers.integrationTitle}</h2>
            <p className="card-sub">{t.developers.integrationSub}</p>
          </div>
        </div>
        <a className="btn btn-ghost" href="https://icryptonow.com/docs/checkout/" target="_blank" rel="noreferrer noopener">
          {t.developers.integrationDocs} <IconExternal size={14} />
        </a>
      </div>
      {q.error ? (
        <Alert tone="error">{errorMessage(q.error)}</Alert>
      ) : q.loading ? (
        <span className="sk sk-lg" />
      ) : (
        <div className="integration-options">
          {options.map((o) => (
            <div key={o.kind} className={`integration-option ${current === o.kind ? "on" : ""}`}>
              <div className="integration-option-head">
                <strong>{name(o.kind)}</strong>
                {current === o.kind ? (
                  <StatusPill status="active" label={t.developers.integrationCurrent} />
                ) : write ? (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSwitchTo(o.kind)}>{t.developers.integrationUse}</button>
                ) : null}
              </div>
              <p className="muted small">{rich(o.body, { code: (c) => <code>{c}</code> })}</p>
            </div>
          ))}
        </div>
      )}
      <StepUpDialog
        open={switchTo !== null}
        onClose={() => setSwitchTo(null)}
        title={switchTo ? fmt(t.developers.integrationConfirmTitle, { name: name(switchTo) }) : ""}
        description={switchTo === "hosted" ? t.developers.integrationConfirmHosted : t.developers.integrationConfirmApi}
        confirmLabel={t.developers.integrationUse}
        onConfirm={async (code) => {
          await api.put("/integration", { integration: switchTo, code });
          toast(t.developers.integrationSaved);
          await q.reload();
        }}
      />
    </section>
  );
}

// ---------------- API keys ----------------

function ApiKeys() {
  const { can } = useSession();
  const t = useT();
  const q = useAsync(() => api.get<{ items: ApiKey[] }>("/api-keys"), []);
  const [creating, setCreating] = useState(false);
  const [revoke, setRevoke] = useState<ApiKey | null>(null);
  const [editing, setEditing] = useState<ApiKey | null>(null);
  const [revealing, setRevealing] = useState<ApiKey | null>(null);
  const toast = useToast();
  const keys = q.data?.items ?? [];
  const kp = useClientPages(keys, "keys_");

  const cols: Column<ApiKey>[] = [
    { key: "id", header: t.developers.colKeyId, cell: (k) => <Hash value={k.key_id} full />, mobile: "title", className: "keep-line" },
    { key: "secret", header: t.developers.colSecret, cell: (k) => <code className="secret-hint" dir="ltr">{maskHint(k.secret_hint)}</code> },
    {
      key: "ips", header: t.developers.colIpAllowlist, cell: (k) => (
        <span className="chips">
          {k.ip_allowlist.length
            ? k.ip_allowlist.map((ip) => <code key={ip} className="chip">{ip}</code>)
            : <span className="pill pill-amber no-dot any-ip" title={t.developers.anyIpTitle}>{t.developers.anyIp}</span>}
        </span>
      ),
    },
    {
      key: "access", header: t.developers.colAccess, cell: (k) => (
        <span className={`pill ${k.can_payout ? "pill-blue" : "pill-grey"} no-dot`} title={k.can_payout ? t.developers.accessPayoutsTitle : t.developers.accessReadOnlyTitle}>
          {k.can_payout ? t.developers.accessPayouts : t.developers.accessReadOnly}
        </span>
      ),
    },
    {
      key: "created", header: t.developers.colCreated, cell: (k) => (
        <span className="stack-cell">
          <Time iso={k.created_at} />
          <small className="muted">{fmt(t.developers.createdBy, { name: k.created_by })}</small>
        </span>
      ),
    },
    { key: "used", header: t.developers.colLastUsed, className: "nowrap", cell: (k) => <span title={k.last_used_at ? formatTimeFull(k.last_used_at) : undefined}>{timeAgo(k.last_used_at)}</span> },
    { key: "status", header: t.common.status, cell: (k) => <StatusPill status={k.status} label={k.status === "revoked" ? fmt(t.developers.revokedAt, { time: formatTime(k.revoked_at) }) : undefined} /> },
  ];
  if (can("api_keys:revoke") || can("api_keys:edit") || can("api_keys:reveal"))
    cols.push({
      key: "act", header: t.developers.colActions, align: "right", cell: (k) => {
        if (k.status !== "active") return null;
        // Changing the IPs of a key that can send payouts also needs payouts:create.
        const editBlocked = k.can_payout && !can("payouts:create");
        return (
          <span className="btn-row tight">
            {can("api_keys:edit") && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(k)} disabled={editBlocked}
                title={editBlocked ? t.developers.editIpsBlocked : undefined}>
                {t.developers.editIps}
              </button>
            )}
            {can("api_keys:reveal") && (
              // A payout key's secret also needs payouts:create (same rule as creating one).
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRevealing(k)} disabled={k.can_payout && !can("payouts:create")}
                title={k.can_payout && !can("payouts:create") ? t.developers.showSecretBlocked : undefined}>
                {t.developers.showSecret}
              </button>
            )}
            {can("api_keys:revoke") && <button type="button" className="btn btn-sm btn-ghost btn-danger-ghost" onClick={() => setRevoke(k)}>{t.developers.revoke}</button>}
          </span>
        );
      },
    });

  return (
    <section className="card section-block" aria-labelledby="keys-h">
      <div className="card-head">
        <div className="card-head-title">
          <div>
            <h2 id="keys-h" className="card-title">{t.developers.keysTitle}</h2>
            <p className="card-sub">{t.developers.keysSub}</p>
          </div>
        </div>
        {can("api_keys:create") && <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}><IconPlus size={18} /> {t.developers.createKey}</button>}
      </div>
      {q.error ? (
        <Alert tone="error">{errorMessage(q.error)}</Alert>
      ) : (
        <DataTable caption={t.developers.keysTitle} columns={cols} rows={kp.rows} rowKey={(k) => k.key_id} loading={q.loading} className="flat"
          footer={keys.length > 0 && <Pager page={kp.page} limit={kp.limit} count={kp.rows.length} total={kp.total} onPage={kp.setPage} onLimit={kp.setLimit} />}
          empty={<EmptyState title={t.developers.noKeys}>{t.developers.noKeysBody}</EmptyState>} />
      )}
      {creating && <CreateKey canPayout={can("payouts:create")} onClose={() => setCreating(false)} onCreated={() => void q.reload()} />}
      {revealing && <RevealSecret apiKey={revealing} onClose={() => setRevealing(null)} />}
      {editing && <EditKeyIps apiKey={editing} onClose={() => setEditing(null)} onSaved={() => { toast(t.developers.ipsUpdated); void q.reload(); }} />}
      <StepUpDialog
        open={!!revoke}
        onClose={() => setRevoke(null)}
        title={t.developers.revokeTitle}
        description={<>{rich(t.developers.revokeDesc, {}, { keyId: revoke?.key_id ?? "" })}</>}
        confirmLabel={t.developers.revokeConfirm}
        danger
        onConfirm={async (code) => {
          await api.post(`/api-keys/${encodeURIComponent(revoke?.key_id ?? "")}/revoke`, { code });
          toast(t.developers.keyRevoked);
          void q.reload();
        }}
      />
    </section>
  );
}

const parseIps = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);

function CreateKey({ canPayout, onClose, onCreated }: { canPayout: boolean; onClose: () => void; onCreated: () => void }) {
  const t = useT();
  const [ips, setIps] = useState("");
  const [payouts, setPayouts] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [result, setResult] = useState<{ key_id: string; secret: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const list = parseIps(ips);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (payouts && !list.length) return setError(t.developers.payoutNeedsIp);
    if (code.length !== 6) return setCodeError(t.developers.enterCode);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      setResult(await api.post<{ key_id: string; secret: string }>("/api-keys", { ip_allowlist: list, payouts, code }));
      onCreated();
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.developers.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => (!result || saved ? onClose() : undefined)} busy={busy} title={result ? t.developers.newKeyTitle : t.developers.createKey}
      description={result ? undefined : t.developers.createKeyDesc}>
      {result ? (
        <div className="form-stack">
          <OnceWarning title={t.developers.keySecretCopyNow} body={null} />
          <SecretField label={t.developers.keyIdLabel} value={result.key_id} />
          <SecretField label={t.developers.secretLabel} value={result.secret} />
          <p className="field-hint">
            {rich(payouts ? t.developers.accessPayoutLine : t.developers.accessReadOnlyLine)}
          </p>
          <label className="check">
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-autofocus />
            <span>{t.developers.storedSecret}</span>
          </label>
          <div className="dlg-actions">
            <button type="button" className="btn btn-primary" onClick={onClose} disabled={!saved}>{t.common.done}</button>
          </div>
        </div>
      ) : (
        <form className="form-stack" onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="ips">{t.developers.serverIps} {payouts ? <span className="req-tag">{t.developers.requiredPayoutKeys}</span> : <span className="optional">{t.developers.optionalReadOnlyKeys}</span>}</label>
            <textarea id="ips" rows={4} className="mono-input" value={ips} onChange={(e) => setIps(e.target.value)} placeholder={"203.0.113.10\n198.51.100.0/24"} spellCheck={false}
              aria-invalid={payouts && !list.length ? true : undefined} />
            <p className="field-hint">
              {t.developers.ipsHint} {list.length > 0 ? plural(list.length, t.developers.entries) : payouts ? "" : t.developers.emptyAnyNetwork}
            </p>
          </div>
          <div className={`key-scope ${canPayout ? "" : "disabled"}`}>
            <label className="check">
              <input type="checkbox" checked={payouts} onChange={(e) => setPayouts(e.target.checked)} disabled={!canPayout} aria-describedby="scope-hint" />
              <span>
                <strong>{t.developers.allowPayouts}</strong>
                <small id="scope-hint">
                  {canPayout
                    ? t.developers.allowPayoutsHint
                    : t.developers.allowPayoutsBlocked}
                </small>
              </span>
            </label>
          </div>
          <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />
          {error && <Alert tone="error">{error}</Alert>}
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={busy || (payouts && !list.length)}>{busy && <Spinner />} {t.developers.createKeyBtn}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// ---------------- Webhook endpoint ----------------

function Webhook({ write }: { write: boolean }) {
  const q = useAsync(() => api.get<{ endpoint: WebhookEndpoint | null }>("/webhook"), []);
  const [editing, setEditing] = useState(false);
  const ep = q.data?.endpoint ?? null;
  const t = useT();
  return (
    <section className="card section-block" aria-labelledby="wh-h">
      <div className="card-head">
        <div className="card-head-title">
          <div>
            <h2 id="wh-h" className="card-title">{t.developers.webhookTitle}</h2>
            <p className="card-sub">{t.developers.webhookSub}</p>
          </div>
        </div>
        {write && <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)}>{ep ? t.developers.changeUrl : t.developers.setUrl}</button>}
      </div>
      {q.error ? (
        <Alert tone="error">{errorMessage(q.error)}</Alert>
      ) : q.loading ? (
        <span className="sk sk-lg" />
      ) : ep ? (
        <div className="endpoint-box">
          <code className="endpoint-url">{ep.url}</code>
          <span className="muted small">{fmt(t.developers.urlSetAt, { time: formatTime(ep.created_at) })}</span>
        </div>
      ) : (
        <Alert tone="warn">{t.developers.noWebhook}</Alert>
      )}
      {editing && <SetWebhook current={ep?.url ?? ""} onClose={() => setEditing(false)} onSaved={() => void q.reload()} />}
    </section>
  );
}

function SetWebhook({ current, onClose, onSaved }: { current: string; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const [url, setUrl] = useState(current);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [result, setResult] = useState<{ url: string; secret: string } | null>(null);
  const [saved, setSaved] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^https:\/\/\S+$/i.test(url.trim())) return setError(t.developers.useHttps);
    if (code.length !== 6) return setCodeError(t.developers.enterCode);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      setResult(await api.put<{ url: string; secret: string }>("/webhook", { url: url.trim(), code }));
      onSaved();
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.developers.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => (!result || saved ? onClose() : undefined)} busy={busy} title={result ? t.developers.signingSecretTitle : current ? t.developers.changeWebhookUrl : t.developers.setWebhookUrl}
      description={result ? undefined : t.developers.savingNewSecret}>
      {result ? (
        <div className="form-stack">
          <OnceWarning what={t.auth.signingSecretWord} />
          <SecretField label={t.developers.endpointLabel} value={result.url} />
          <SecretField label={t.developers.signingSecretLabel} value={result.secret} />
          <label className="check">
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-autofocus />
            <span>{t.developers.updatedServer}</span>
          </label>
          <div className="dlg-actions">
            <button type="button" className="btn btn-primary" onClick={onClose} disabled={!saved}>{t.common.done}</button>
          </div>
        </div>
      ) : (
        <form className="form-stack" onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="wh-url">{t.developers.endpointUrl}</label>
            <input id="wh-url" type="url" className="mono-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.example.com/hooks/icryptonow" spellCheck={false} autoComplete="off" />
          </div>
          {current && <Alert tone="warn">{t.developers.oldUrlWarning}</Alert>}
          <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />
          {error && <Alert tone="error">{error}</Alert>}
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t.developers.saveUrl}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// ---------------- Deliveries ----------------

const EVENT_STATUSES = ["", "pending", "delivered", "failed"] as const;

function Deliveries({ write }: { write: boolean }) {
  const [status, setStatus] = useState("");
  const list = usePagedList<WebhookEvent>("/webhook/events", { status }, "ev_", 25);
  const [open, setOpen] = useState<WebhookEvent | null>(null);
  const t = useT();

  const cols: Column<WebhookEvent>[] = [
    { key: "type", header: t.developers.colEvent, cell: (e) => <code className="event-type">{e.type}</code>, mobile: "title" },
    { key: "status", header: t.common.status, cell: (e) => <StatusPill status={e.status} /> },
    { key: "attempts", header: t.developers.colAttempts, align: "right", cell: (e) => e.attempts },
    { key: "err", header: t.developers.colLastError, cell: (e) => (e.last_error ? <span className="err-text" title={e.last_error}>{e.last_error}</span> : <span className="muted empty-val">–</span>), className: "truncate-cell" },
    { key: "time", header: t.developers.colCreated, cell: (e) => <Time iso={e.created_at} /> },
  ];

  return (
    <section className="card section-block" aria-labelledby="dl-h">
      <div className="card-head">
        <div>
          <h2 id="dl-h" className="card-title">{t.developers.deliveriesTitle}</h2>
          <p className="card-sub">{t.developers.deliveriesSub}</p>
        </div>
        <div className="seg" role="group" aria-label={t.developers.filterByStatus}>
          {EVENT_STATUSES.map((s) => (
            <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)}>{s ? t.common.statuses[s] : t.common.all}</button>
          ))}
        </div>
      </div>
      {list.error ? (
        <Alert tone="error">{errorMessage(list.error)}</Alert>
      ) : (
        <>
          <DataTable caption={t.developers.deliveriesTitle} columns={cols} rows={list.items} rowKey={(e) => e.id} loading={list.loading} onRowClick={setOpen} className="flat"
            rowLabel={(e) => `${e.type}, ${e.status}`} empty={<EmptyState title={t.developers.noEvents}>{t.developers.noEventsBody}</EmptyState>}
            footer={<Pager page={list.page} limit={list.limit} count={list.items.length} total={list.total} hasMore={list.hasMore} onPage={list.setPage} onLimit={list.setLimit} />} />
        </>
      )}
      <EventDrawer event={open} write={write} onClose={() => setOpen(null)} onChanged={(e) => { list.replace(e); }} />
    </section>
  );
}

function EventDrawer({ event, write, onClose, onChanged }: { event: WebhookEvent | null; write: boolean; onClose: () => void; onChanged: (e: WebhookEvent) => void }) {
  const [e, setE] = useState<WebhookEvent | null>(event);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const t = useT();

  useEffect(() => {
    setE(event);
    setError(null);
    if (!event) return;
    let alive = true;
    setLoading(true);
    api.get<WebhookEvent>(`/webhook/events/${encodeURIComponent(event.id)}`)
      .then((x) => alive && setE(x))
      .catch((err) => alive && setError(errorMessage(err)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [event]);

  const resend = async () => {
    if (!e) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<WebhookEvent>(`/webhook/events/${encodeURIComponent(e.id)}/resend`);
      setE((old) => ({ ...r, payload: r.payload ?? old?.payload }));
      onChanged(r);
      toast(t.developers.resent);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!event} onClose={onClose} kind="drawer" size="lg" title={t.developers.eventTitle} description={e ? <code className="event-type">{e.type}</code> : undefined}>
      {e && (
        <>
          <div className="drawer-hero">
            <StatusPill status={e.status} />
            {write && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void resend()} disabled={busy || e.status === "pending"}>
                {busy ? <Spinner /> : <IconRefresh size={16} />} {t.developers.resend}
              </button>
            )}
          </div>
          {error && <Alert tone="error">{error}</Alert>}
          {e.last_error && <Alert tone={e.status === "failed" ? "error" : "info"} title={t.developers.lastError}>{e.last_error}</Alert>}
          <Details
            rows={[
              [t.developers.eventId, <Hash value={e.event_id} full />, true],
              [t.developers.attempts, e.attempts],
              [t.developers.created, formatTimeFull(e.created_at)],
              [t.developers.delivered, e.delivered_at ? formatTimeFull(e.delivered_at) : null],
              [t.developers.nextAttempt, e.next_attempt_at ? formatTimeFull(e.next_attempt_at) : null],
            ]}
          />
          <div className="payload">
            <div className="payload-head">
              <span>{t.developers.payload}</span>
              {e.payload !== undefined && <CopyButton value={JSON.stringify(e.payload, null, 2)} label={t.developers.copyJson} showText className="on-dark" />}
            </div>
            {loading && e.payload === undefined ? (
              <div className="payload-loading"><Spinner /></div>
            ) : (
              <pre className="code-block" tabIndex={0} aria-label={t.developers.payloadAria}>{JSON.stringify(e.payload ?? null, null, 2)}</pre>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
}

/** Change the server IPs of an existing key. Key id and secret stay the same. */
function EditKeyIps({ apiKey, onClose, onSaved }: { apiKey: ApiKey; onClose: () => void; onSaved: () => void }) {
  const { can } = useSession();
  const t = useT();
  const [ips, setIps] = useState(apiKey.ip_allowlist.join("\n"));
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  // "Your current IP" is only a hint (it's the network you browse from, usually not your server). Never added automatically.
  const settings = useAsync(() => (can("settings:view") ? api.get<SettingsData>("/settings") : Promise.resolve(null)), []);
  const list = parseIps(ips);
  const emptyPayout = apiKey.can_payout && !list.length;
  const changed = list.join("\n") !== apiKey.ip_allowlist.join("\n");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (emptyPayout) return setError(t.developers.payoutKeepIp);
    if (code.length !== 6) return setCodeError(t.developers.enterCode);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      await api.put(`/api-keys/${encodeURIComponent(apiKey.key_id)}/ip-allowlist`, { ip_allowlist: list, code });
      onSaved();
      onClose();
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.developers.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} busy={busy} title={t.developers.editIpsTitle} description={<>{rich(t.developers.editIpsDesc, {}, { keyId: apiKey.key_id })}</>}>
      <form className="form-stack" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="edit-ips">{t.developers.serverIps} {apiKey.can_payout ? <span className="req-tag">{t.developers.requiredPayoutKey}</span> : <span className="optional">{t.developers.optionalReadOnlyKey}</span>}</label>
          <textarea id="edit-ips" rows={5} className="mono-input" value={ips} onChange={(e) => setIps(e.target.value)} spellCheck={false}
            placeholder={"203.0.113.10\n198.51.100.0/24"} aria-invalid={emptyPayout || undefined} aria-describedby="edit-ips-hint" />
          <p id="edit-ips-hint" className={emptyPayout ? "field-error" : "field-hint"}>
            {emptyPayout
              ? t.developers.payoutKeepIp
              : list.length
                ? `${t.developers.onePerLine} ${plural(list.length, t.developers.entries)}`
                : t.developers.emptyReadOnlyAnyNetwork}
          </p>
        </div>
        {settings.data?.your_ip && (
          <p className="field-hint your-ip-hint">
            {rich(t.developers.yourIp, {}, { ip: settings.data.your_ip })}
          </p>
        )}
        <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />
        {error && <Alert tone="error">{error}</Alert>}
        <div className="dlg-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.common.cancel}</button>
          <button type="submit" className="btn btn-primary" disabled={busy || emptyPayout || !changed}>{busy && <Spinner />} {t.developers.saveIps}</button>
        </div>
      </form>
    </Dialog>
  );
}

/** "AbCd…WxYz" → "AbCd••••••••WxYz"; no hint → "••••••••". */
const maskHint = (hint: string | null | undefined) => {
  const m = hint ? /^(.{1,8})…(.{1,8})$/.exec(hint) : null;
  return m ? `${m[1]}••••••••${m[2]}` : "••••••••";
};

const REVEAL_SECONDS = 30;

/** View a key's secret again: 2FA, then the secret for 30 s (cleared from memory when it hides or the dialog closes). */
function RevealSecret({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const t = useT();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [left, setLeft] = useState(0);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!secret) return;
    const timer = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) {
          setSecret(null);
          setHidden(true);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [secret]);

  const close = () => {
    setSecret(null);
    setCode("");
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return setCodeError(t.common.errors.mfa_required);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const r = await api.post<{ key_id: string; secret: string }>(`/api-keys/${encodeURIComponent(apiKey.key_id)}/reveal`, { code });
      setSecret(r.secret);
      setLeft(REVEAL_SECONDS);
      setHidden(false);
    } catch (err) {
      if (isCode(err, "mfa_required")) setCodeError(t.common.errors.mfa_required);
      else setError(errorMessage(err));
    } finally {
      setCode("");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={close} busy={busy} title={fmt(t.developers.revealTitle, { keyId: apiKey.key_id })} description={secret ? undefined : t.developers.revealDesc}>
      {secret ? (
        <div className="form-stack">
          <SecretField label={t.developers.secretLabel} value={secret} />
          <div className="reveal-timer" aria-live="polite">
            <div className="usage-bar"><span style={{ inlineSize: `${(left / REVEAL_SECONDS) * 100}%` }} /></div>
            <span className="muted small">{fmt(t.developers.revealHidesIn, { n: left })}</span>
          </div>
          <p className="field-hint">{t.developers.revealLogged}</p>
          <div className="dlg-actions">
            <button type="button" className="btn btn-primary" onClick={close} data-autofocus>{t.common.done}</button>
          </div>
        </div>
      ) : (
        <form className="form-stack" onSubmit={submit} noValidate>
          {hidden && <Alert tone="info">{t.developers.revealHidden}</Alert>}
          <CodeInput value={code} onChange={(c) => { setCode(c); setCodeError(null); }} error={codeError} disabled={busy} autoFocus />
          {error && <Alert tone="error">{error}</Alert>}
          <p className="field-hint">{t.developers.revealLogged}</p>
          <div className="dlg-actions">
            <button type="button" className="btn btn-ghost" onClick={close} disabled={busy}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t.developers.showSecret}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
